import type { PixelEvent } from '@/events';

import type { DestinationAdapter, DestinationResult } from '../types';

/** Events typically relevant for conversion / commerce destinations. */
export const DEFAULT_COMMERCE_EVENT_TYPES = new Set([
  'PAGE_VIEW',
  'PAGE_CLICK',
  'SEARCH',
  'LEAD',
  'INITIATE_CHECKOUT',
  'PURCHASE',
  'ADD_PAYMENT_INFO',
  'ADD_TO_CART',
  'REMOVE_FROM_CART',
  'VIEW_CONTENT',
]);

export type BaseDestinationOptions = {
  readonly name: string;
  readonly version?: string;
  readonly supportedTypes?: ReadonlySet<string>;
};

/**
 * Shared helpers for destination adapters — each subclass owns supports() subset
 * and builds its own external payload without mutating PixelEvent.
 */
export abstract class BaseDestinationAdapter implements DestinationAdapter {
  readonly name: string;

  readonly version: string;

  protected readonly supportedTypes: ReadonlySet<string>;

  constructor(options: BaseDestinationOptions) {
    this.name = options.name;
    this.version = options.version ?? '1.0.0';
    this.supportedTypes =
      options.supportedTypes ?? DEFAULT_COMMERCE_EVENT_TYPES;
  }

  supports(event: PixelEvent): boolean {
    return this.supportedTypes.has(event.type);
  }

  abstract execute(context: {
    readonly event: PixelEvent;
    readonly signal: AbortSignal;
    readonly timeoutMs: number;
    readonly debug: boolean;
  }): Promise<DestinationResult>;

  protected createResult(
    status: DestinationResult['status'],
    duration: number,
    extras?: Partial<DestinationResult>
  ): DestinationResult {
    return {
      status,
      adapter: this.name,
      duration,
      timestamp: Date.now(),
      ...extras,
    };
  }
}
